import apiClient from './apiClient';

const billingService = {
  // Dashboard Metrics
  getDashboardMetrics: async (params = {}) => {
    try {
      return await apiClient.get('/billing/api/dashboard/metrics', { params });
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch billing metrics');
    }
  },

  // Customers (Billing specific if needed, or reuse existing)
  getCustomers: async (params = {}) => {
    try {
      return await apiClient.get('/billing/api/customers', { params });
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch customers');
    }
  },

  createCustomer: async (data) => {
    try {
      return await apiClient.post('/billing/api/customers/create', data);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to create customer');
    }
  },

  // Products
  getProducts: async () => {
    try {
      return await apiClient.get('/billing/api/products');
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch products');
    }
  },

  createProduct: async (data) => {
    try {
      return await apiClient.post('/billing/api/products', data);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to create product');
    }
  },

  updateProduct: async (id, data) => {
    try {
      return await apiClient.put(`/billing/api/products/${id}`, data);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to update product');
    }
  },

  deleteProduct: async (id) => {
    try {
      return await apiClient.delete(`/billing/api/products/${id}`);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to delete product');
    }
  },

  updateCustomer: async (id, data) => {
    try {
      return await apiClient.put(`/billing/api/customers/${id}`, data);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to update customer');
    }
  },

  deleteCustomer: async (id) => {
    try {
      return await apiClient.delete(`/billing/api/customers/${id}`);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to delete customer');
    }
  },

  getCustomerById: async (id) => {
    try {
      return await apiClient.get(`/billing/api/customers/${id}`);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch customer details');
    }
  },

  getPaymentsByCustomer: async (customerId) => {
    try {
      return await apiClient.get(`/billing/api/payments/customer/${customerId}`);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch customer payments');
    }
  },

  addPayment: async (data) => {
    try {
      return await apiClient.post('/billing/api/payments', data);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to add payment');
    }
  },

  updatePayment: async (id, data) => {
    try {
      return await apiClient.put(`/billing/api/payments/${id}`, data);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to update payment');
    }
  },

  // Quotes
  getQuotes: async (params = {}) => {
    try {
      return await apiClient.get(`/billing/api/quotes/customer`, { params });
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch quotes');
    }
  },

  getQuoteById: async (id) => {
    try {
      return await apiClient.get(`/billing/api/quotes/${id}`);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch quote details');
    }
  },

  createQuote: async (data) => {
    try {
      return await apiClient.post('/billing/api/quotes', data);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to create quote');
    }
  },

  updateQuote: async (id, data) => {
    try {
      return await apiClient.put(`/billing/api/quotes/${id}`, data);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to update quote');
    }
  },

  deleteQuote: async (id) => {
    try {
      return await apiClient.delete(`/billing/api/quotes/${id}`);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to delete quote');
    }
  },

  // Followups & Feedback
  getFollowups: async (params = {}) => {
    try {
      return await apiClient.get('/billing/api/followups', { params });
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch followups');
    }
  },

  createFollowup: async (data) => {
    try {
      return await apiClient.post('/billing/api/followups', data);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to create followup');
    }
  },

  updateFollowup: async (id, data) => {
    try {
      return await apiClient.put(`/billing/api/followups/${id}`, data);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to update followup');
    }
  },

  getQuoteByPublicId: async (publicId) => {
    try {
      return await apiClient.get(`/billing/api/quotes/public/${publicId}`);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch public quote');
    }
  },

  // Subscriptions
  getSubscriptions: async (params = {}) => {
    try {
      return await apiClient.get('/billing/api/subscriptions', { params });
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch subscriptions');
    }
  },

  getSubscriptionById: async (id) => {
    try {
      return await apiClient.get(`/billing/api/subscriptions/${id}`);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch subscription details');
    }
  },

  createSubscription: async (data) => {
    try {
      return await apiClient.post('/billing/api/subscriptions', data);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to create subscription');
    }
  },

  updateSubscription: async (id, data) => {
    try {
      return await apiClient.put(`/billing/api/subscriptions/${id}`, data);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to update subscription');
    }
  },

  cancelSubscription: async (id, reason) => {
    try {
      return await apiClient.post(`/billing/api/subscriptions/${id}/cancel`, { reason });
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to cancel subscription');
    }
  },

  deleteSubscription: async (id) => {
    try {
      return await apiClient.delete(`/billing/api/subscriptions/${id}`);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to delete subscription');
    }
  },

  // Invoices
  getInvoices: async () => {
    try {
      return await apiClient.get('/billing/api/invoices');
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch invoices');
    }
  },

  getInvoiceById: async (id) => {
    try {
      return await apiClient.get(`/billing/api/invoices/${id}`);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch invoice details');
    }
  },

  recordPayment: async (id, paymentData) => {
    try {
      return await apiClient.post(`/billing/api/invoices/${id}/payments`, paymentData);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to record payment');
    }
  },

  // Reports
  getMRRReport: async () => {
    try {
      return await apiClient.get('/billing/api/reports/mrr');
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch MRR report');
    }
  },

  getRevenueReport: async () => {
    try {
      return await apiClient.get('/billing/api/reports/revenue');
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch revenue report');
    }
  },

  getOutstandingInvoices: async () => {
    try {
      return await apiClient.get('/billing/api/reports/outstanding');
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch outstanding invoices');
    }
  },

  triggerRenewals: async () => {
    try {
      return await apiClient.post('/billing/api/admin/trigger-renewals');
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to trigger renewals');
    }
  },

  // Uploads & Logs
  getUploadLogs: async (params = {}) => {
    try {
      return await apiClient.get('/billing/api/upload/logs', { params });
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch upload logs');
    }
  },

  uploadCustomers: async (records) => {
    try {
      return await apiClient.post('/billing/api/upload/customers', { records });
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to start customer upload');
    }
  },

  uploadSales: async (records) => {
    try {
      return await apiClient.post('/billing/api/upload/sales', { records });
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to start sales upload');
    }
  },

  getTicketMappings: async (params = {}) => {
    try {
      return await apiClient.get('/billing/api/ticket-mappings', { params });
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch ticket mappings');
    }
  },

  createTicketMapping: async (data) => {
    try {
      return await apiClient.post('/billing/api/ticket-mappings', data);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to create ticket mapping');
    }
  },

  updateTicketMapping: async (id, data) => {
    try {
      return await apiClient.put(`/billing/api/ticket-mappings/${id}`, data);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to update ticket mapping');
    }
  },

  // Settings
  getSettings: async () => {
    try {
      return await apiClient.get('/billing/api/settings');
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch settings');
    }
  },

  updateSettings: async (data) => {
    try {
      return await apiClient.put('/billing/api/settings', data);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to update settings');
    }
  },

  sendTemplateMessage: async (template, variables) => {
    try {
      return await apiClient.post('/billing/api/settings/send-template', { template, variables });
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to send message');
    }
  }
};

export default billingService;
